import {
  getQueryKey,
  queryClient,
  useApi,
  useGetQuery,
  usePostQuery,
  usePutQuery,
  useRouteParams
} from "@quortex/front-common"
import { useQuery } from "@tanstack/react-query"
import type { DeepPartial } from "react-hook-form"
import type { SegmentListParams, SegmentParams } from "@/queries/types"
import type { SegmentRequest, SegmentResponse } from "@/types/api"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

const getSegmentListUrl = ({ workspaceUuid, orgUuid, dryRun }: Required<SegmentListParams>) =>
  `/workspaces/${workspaceUuid}/segments/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

const getSegmentUrl = ({ workspaceUuid, segmentUuid, orgUuid, dryRun }: Required<SegmentParams>) =>
  `/workspaces/${workspaceUuid}/segments/${segmentUuid}/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

const getSegmentCheckUrl = ({
  currentSegmentUuid,
  orgUuid,
  segmentUuid,
  workspaceUuid
}: Required<SegmentParams>) =>
  `/workspaces/${workspaceUuid}/segments/${segmentUuid}/check/?org=${orgUuid}${currentSegmentUuid ? `&current_segment_uuid=${currentSegmentUuid}` : ""}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useSegmentList = (params?: SegmentListParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getSegmentListUrl(mergedParams)

  return useGetQuery<SegmentResponse[]>("switch", listUrl)
}

export const useSegment = (params?: SegmentParams) => {
  const mergedParams = useRouteParams(params)
  const url = getSegmentUrl(mergedParams)

  return useGetQuery<SegmentResponse>("switch", url)
}

export const useAddSegment = (params?: SegmentParams) => {
  const mergedParams = useRouteParams(params)
  const url = getSegmentListUrl(mergedParams)
  const { jsonPost } = useApi("switch")

  type TRequest = DeepPartial<SegmentRequest>
  type TResponse = SegmentResponse

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async (data: TResponse) => {
      const [path, queryString] = url.split("?")
      queryClient.setQueryData(getQueryKey("switch", `${path}/${data.uuid}/?${queryString}`), data)
      const parentUuid = "parentUuid" in data ? data.parentUuid : undefined
      const toRefetchUrls = [
        url,
        ...(parentUuid ? [getSegmentUrl({ ...mergedParams, segmentUuid: parentUuid })] : []),
        ...(data.childrenUuids
          ? data.childrenUuids.map(childUuid =>
              getSegmentUrl({ ...mergedParams, segmentUuid: childUuid })
            )
          : [])
      ]

      await Promise.all([
        ...toRefetchUrls.map(toRefetchUrl =>
          queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("switch", toRefetchUrl) })
        )
      ])
    }
  } as const
}

export const useCheckSegment = (params?: SegmentParams) => {
  const mergedParams = useRouteParams(params)
  const url = getSegmentCheckUrl(mergedParams)

  type TRequest = DeepPartial<api.switchns.SegmentCheckRequest>
  type TResponse = api.switchns.SegmentCheck & { uuid: never }

  return usePostQuery<TRequest, TResponse>("switch", url, [url])
}

export const useUpdateSegment = (params?: SegmentParams) => {
  const mergedParams = useRouteParams(params)
  const url = getSegmentUrl(mergedParams)
  type TRequest = DeepPartial<SegmentRequest>
  type TResponse = SegmentResponse

  return usePutQuery<TRequest, TResponse>("switch", url, [url])
}

export const useDeleteSegment = (params?: SegmentParams) => {
  const mergedParams = useRouteParams(params)
  const url = getSegmentUrl(mergedParams)
  const listUrl = getSegmentListUrl(mergedParams)
  const { data } = useQuery({ ...useSegment(mergedParams), throwOnError: false })

  const { jsonDelete } = useApi("switch")
  const parentUuid = data && "parentUuid" in data ? data.parentUuid : undefined

  return {
    mutationFn: () => jsonDelete(url),
    onSuccess: async () => {
      const toRefetchUrls = [
        url,
        listUrl,
        ...(parentUuid ? [getSegmentUrl({ ...mergedParams, segmentUuid: parentUuid })] : [])
      ]
      await Promise.all([
        ...toRefetchUrls.map(toRefetchUrl =>
          queryClient.refetchQueries({ exact: true, queryKey: getQueryKey("switch", toRefetchUrl) })
        )
      ])

      setTimeout(() => queryClient.removeQueries({ queryKey: getQueryKey("switch", url) }), 1_000)
    }
  } as const
}
